<template>
    <div class="w-full max-w-md">
        <!-- Loading skeleton -->
        <div
            v-if="isLoading"
            class="flex flex-col divide-y"
        >
            <div
                v-for="idx in 5"
                :key="idx"
                class="flex w-full gap-4 px-2 py-4"
            >
                <div class="mt-2 h-3 w-3 shrink-0 animate-pulse rounded-full bg-gray-200"></div>
                <div class="flex grow flex-col gap-2">
                    <div class="h-4 w-3/4 animate-pulse rounded-sm bg-gray-200"></div>
                    <div class="h-3 w-1/3 animate-pulse rounded-sm bg-gray-200"></div>
                </div>
            </div>
        </div>

        <!-- Notification list -->
        <div
            v-else
            class="flex h-full w-full flex-col divide-y"
        >
            <template v-if="notifications.length">
                <NotificationItem
                    v-for="(notification, index) in notifications"
                    :key="notification.id ?? index"
                    :notification="notification"
                    @notification-clicked="handleClick"
                    @mark-read="markRead"
                />
            </template>

            <!-- Empty state -->
            <div
                v-else
                class="text-muval-gray-3 flex items-center justify-center py-12 text-sm"
            >
                {{ emptyText }}
            </div>

            <!-- Loading next page -->
            <div
                v-if="isFetchingNextPage"
                class="flex items-center justify-center py-4"
            >
                <SvgIcon
                    name="spinner"
                    class="text-muval-gray-3 h-5 w-5 animate-spin"
                />
            </div>

            <!-- End of list -->
            <div
                v-if="!hasNextPage && notifications.length"
                class="text-muval-gray-3 flex items-center justify-center py-4 text-xs"
            >
                No more notifications
            </div>
        </div>
    </div>
</template>

<script setup>
import { useQueryClient } from '@tanstack/vue-query';
import { useNotificationsStore } from '@store/notifications';
import { buildNotificationRoute } from '@utilities/notificationActions';

const props = defineProps({
    notifications: {
        type: Array,
        required: true,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    isFetchingNextPage: {
        type: Boolean,
        default: false,
    },
    hasNextPage: {
        type: Boolean,
        default: false,
    },
    scope: {
        type: String,
        default: 'main',
        validator: (value) => ['main', 'visitor'].includes(value),
    },
    emptyText: {
        type: String,
        default: 'No notifications',
    },
});

const notificationsStore = useNotificationsStore();
const queryClient = useQueryClient();
const router = useRouter();
const emit = defineEmits(['close']);

const handleClick = async (notification) => {
    // Build route from notification data
    const route = buildNotificationRoute(notification);

    // Mark as read optimistically (updates local state without refetching)
    if (!notification.read_at) {
        notificationsStore.markAsReadOptimistic(notification.id, props.scope);

        // Also update the query cache optimistically
        queryClient.setQueryData(['notifications', props.scope], (old) => {
            if (!old) return old;
            return {
                ...old,
                pages: old.pages.map((page) => ({
                    ...page,
                    data: page.data.map((n) =>
                        n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n,
                    ),
                })),
            };
        });
    }

    // Navigate to the route
    if (route) {
        try {
            await router.push(route);
        } catch (error) {
            console.error('Navigation failed:', error);
            // Still close the panel even if navigation fails
        }
    }

    // Close panel after navigation
    emit('close');
};

async function markRead(notification) {
    if (!notification.read_at) {
        await notificationsStore.read(notification.id, props.scope);
    }
}
</script>
